/**
 * PermissionsContext — single source of truth for the signed-in user's
 * effective permissions.
 *
 * On mount, fetches /api/v1/rbac/my-permissions and exposes:
 *   - permissions: Set<string>
 *   - roles: string[]
 *   - has(key): boolean
 *   - hasAny(keys): boolean
 *   - hasAll(keys): boolean
 *   - isLoading: boolean
 *   - reload(): refetch (call after role/override changes)
 *   - simulatedRole: the role being simulated (null if none)
 *
 * Wraps RBACProvider — RBAC still owns legacy role string, we extend.
 */
import { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { useMsal, useAccount } from '@azure/msal-react';
import api from '../lib/api';
import { useUser } from '../lib/auth';
import { accountIsFounder } from '../lib/founders';

interface PermissionsContextValue {
  permissions: Set<string>;
  roles: string[];
  has: (key: string) => boolean;
  hasAny: (keys: string[]) => boolean;
  hasAll: (keys: string[]) => boolean;
  isLoading: boolean;
  reload: () => Promise<void>;
  simulatedRole: string | null;
  startSimulation: (roleKey: string) => Promise<void>;
  endSimulation: () => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextValue>({
  permissions: new Set(),
  roles: [],
  has: () => false,
  hasAny: () => false,
  hasAll: () => false,
  isLoading: true,
  reload: async () => {},
  simulatedRole: null,
  startSimulation: async () => {},
  endSimulation: async () => {},
});

export function PermissionsProvider({ children }: { children: ReactNode }) {
  const { isSignedIn, isLoaded } = useUser();
  const [permissions, setPermissions] = useState<Set<string>>(new Set());
  const [roles, setRoles] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [simulatedRole, setSimulatedRole] = useState<string | null>(null);
  const [simPermissions, setSimPermissions] = useState<Set<string> | null>(null);

  // Reload guards. The QA report flagged that during MSAL boot the
  // `isSignedIn` flag flickers as accounts/tokens settle, and every
  // flicker re-fired this effect — three concurrent /rbac/my-permissions
  // hits would trip the rate limiter and send the user a 429, with no
  // recovery. These refs dedupe in-flight reloads and back off after a
  // failure (especially 429) so we don't make the rate-limit problem
  // worse by retrying immediately.
  const inFlightRef = useRef(false);
  const cooldownUntilRef = useRef(0);

  const reload = useCallback(async () => {
    if (!isSignedIn) {
      setPermissions(new Set());
      setRoles([]);
      setIsLoading(false);
      return;
    }
    // Cooldown after a 429 / 401 — give the rate limiter (and the user's
    // session) time to recover instead of hammering. Plain return so the
    // caller doesn't see an exception; isLoading stays whatever it was.
    if (Date.now() < cooldownUntilRef.current) return;
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const res = await api.get<{ permissions: string[]; roles: string[] }>('/rbac/my-permissions');
      setPermissions(new Set(res.data.permissions));
      setRoles(res.data.roles);

      // Check for active simulation session
      const simRes = await api.get<{ active: { simulated_role: string } | null }>('/rbac/simulate/current');
      if (simRes.data.active) {
        setSimulatedRole(simRes.data.active.simulated_role);
        // Fetch simulated role's permissions
        const rp = await api.get<{ permissions: string[] }>(`/rbac/simulate/permissions/${simRes.data.active.simulated_role}`);
        setSimPermissions(new Set(rp.data.permissions));
      } else {
        setSimulatedRole(null);
        setSimPermissions(null);
      }
    } catch (err: any) {
      console.error('[permissions] reload failed:', err);
      // Esther's screenshot: the page flashes white because a 429
      // (initial-load rate-limit storm) caused us to wipe the
      // existing permission set, which made every gated render
      // collapse to "no access" for a moment until the next reload
      // succeeded. Fix: NEVER wipe perms on a transient error —
      // keep the previous values so the UI stays stable. Only the
      // "we're explicitly signed out" branch above clears perms.
      // Backoff windows tuned to the actual failure mode:
      //   429 → 30s (rate limiter resets quickly but we don't know
      //              the exact window)
      //   401 → 15s (token bad — likely needs refresh, no point
      //              retrying immediately)
      //   else → 5s (generic transient backoff)
      const status = err?.response?.status;
      const cooldownMs =
        status === 429 ? 30_000 :
        status === 401 ? 15_000 :
                          5_000;
      cooldownUntilRef.current = Date.now() + cooldownMs;
    } finally {
      inFlightRef.current = false;
      setIsLoading(false);
    }
  }, [isSignedIn]);

  useEffect(() => {
    if (isLoaded) void reload();
  }, [isLoaded, reload]);

  // Founder failsafe — platform owners always have full access in the UI,
  // even if /rbac/my-permissions fails or is rate-limited. The backend
  // enforces the same allowlist on real API calls. Disabled while simulating
  // another role so "view as role" still works for owners.
  const { accounts } = useMsal();
  const account = useAccount(accounts[0] ?? null);
  const isFounder = accountIsFounder(account) && !simulatedRole;

  // The set used for checks — use simulated role's perms when simulating
  const effective = simPermissions ?? permissions;

  const has = useCallback((key: string) => isFounder || effective.has(key), [effective, isFounder]);
  const hasAny = useCallback((keys: string[]) => isFounder || keys.some(k => effective.has(k)), [effective, isFounder]);
  const hasAll = useCallback((keys: string[]) => isFounder || keys.every(k => effective.has(k)), [effective, isFounder]);

  // Surface 'ceo' as the role for founders so the UI reflects full access.
  // Memoized: without this the founder branch built a new array every render,
  // breaking the value memo below and re-rendering every permission consumer
  // (Sidebar, gates, banners) on the owner's account specifically.
  // Founders and platform admins read as CEO to role-name checks. Only the
  // role list is widened — has() still answers from the server's permission
  // set, so a failed /my-permissions call keeps failing closed.
  const effectiveRoles = useMemo(
    () => ((isFounder || roles.includes('platform_admin')) ? Array.from(new Set(['ceo', ...roles])) : roles),
    [isFounder, roles],
  );

  const startSimulation = useCallback(async (roleKey: string) => {
    await api.post('/rbac/simulate/start', { simulated_role: roleKey });
    await reload();
  }, [reload]);

  const endSimulation = useCallback(async () => {
    await api.post('/rbac/simulate/end');
    await reload();
  }, [reload]);

  const value = useMemo<PermissionsContextValue>(() => ({
    permissions: effective,
    roles: effectiveRoles,
    has, hasAny, hasAll,
    isLoading,
    reload,
    simulatedRole,
    startSimulation,
    endSimulation,
  }), [effective, effectiveRoles, has, hasAny, hasAll, isLoading, reload, simulatedRole, startSimulation, endSimulation]);

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
}

export function usePermissions() {
  return useContext(PermissionsContext);
}

/** Shortcut hook — returns just the boolean. */
export function useCan(key: string): boolean {
  return useContext(PermissionsContext).has(key);
}

/** Requires ANY of the listed permissions. */
export function useCanAny(keys: string[]): boolean {
  return useContext(PermissionsContext).hasAny(keys);
}

/** Requires ALL of the listed permissions. */
export function useCanAll(keys: string[]): boolean {
  return useContext(PermissionsContext).hasAll(keys);
}
